/**
 * Shared write helper: merge-write a document, but replace some top-level
 * fields as WHOLE values instead of deep-merging them.
 *
 * Firestore's `merge: true` recurses into nested maps, so a key the new value
 * lacks survives the write. Extracted from `ThemeV2Service.update`
 * (AMV-AC-004 review fix, controller ruling R-13, commit `a4188548b`): a
 * theme's `animationSettings` is a scheme SNAPSHOT — after Smooth → None the
 * stored theme kept Smooth's `perKindOverrides.Paywall`, because a plain
 * `merge: true` write never touches a path the new value omits. The same
 * defect exists on `ProjectThemeService.update` (F4, ruling R-14).
 *
 * @module firebase-core/utils
 */

import type { DocumentData, DocumentReference, WriteBatch } from 'firebase/firestore';

/**
 * Adds two writes to `batch` (the caller commits it):
 * 1. every field of `data` NOT in `fields`, with `merge: true` — today's
 *    deep-merge semantics, unchanged;
 * 2. ONE additional `mergeFields` write covering exactly the entries of
 *    `fields` that are present in `data` (`!== undefined`) — a wholesale
 *    replacement of those paths — issued only when at least one is present.
 *
 * A listed field that is `undefined`, or simply absent from `data`, is left
 * untouched by BOTH writes: Firestore's `mergeFields` throws when a masked
 * path is missing from the write payload, so it is dropped from the mask and
 * the payload, exactly like an ordinary `merge: true` write already leaves an
 * absent key alone.
 *
 * Both writes are `batch.set` calls on the SAME `docRef`, so a bound
 * `FirestoreDataConverter`'s `toFirestore(data, options)` still runs for
 * each — never `updateDoc` — matching this module's "set with merge, never
 * updateDoc" rule for every service, converter-bound (`ThemeV2Service`) or
 * plain (`ProjectThemeService`).
 *
 * @param batch - an open WriteBatch; the caller commits it (this function
 *   never calls `commit()`, so callers stay in control of atomicity/timing)
 * @param docRef - the document reference: converter-bound or plain
 * @param data - the partial update; only OWN enumerable keys are read
 * @param fields - the top-level keys to replace wholesale instead of merging
 * @param prepareData - optional transform applied to EACH write's payload
 *   (the "everything else" merge payload AND the replacement payload) before
 *   it is sent — e.g. the shared `stripUndefined` (`firebase-core/utils`). A
 *   converter-bound reference is NOT exempt: `FirestoreDataConverter.toFirestore`
 *   (see `themeV2Converter.ts`) strips `undefined` at the TOP LEVEL only — a
 *   NESTED `undefined` (e.g. a theme's `animationSettings.transition.direction`
 *   reset to "Default", which `structuredClone` keeps as an own key) reaches
 *   the SDK unstripped and throws synchronously, before `commit()` (interim
 *   review I-1). Pass `stripUndefined` regardless of whether the reference is
 *   converter-bound. Defaults to identity.
 */
export function mergeWriteReplacingFields<AppModelType, DbModelType extends DocumentData = DocumentData>(
  batch: WriteBatch,
  docRef: DocumentReference<AppModelType, DbModelType>,
  data: Partial<AppModelType>,
  fields: ReadonlyArray<Extract<keyof AppModelType, string>>,
  prepareData: (value: Partial<AppModelType>) => Partial<AppModelType> = (value) => value
): void {
  const replaceable = new Set<string>(fields);
  const rest: Record<string, unknown> = {};
  const replacement: Record<string, unknown> = {};
  const presentFields: Array<Extract<keyof AppModelType, string>> = [];

  Object.entries(data).forEach(([key, value]) => {
    if (replaceable.has(key)) {
      if (value !== undefined) {
        replacement[key] = value;
        presentFields.push(key as Extract<keyof AppModelType, string>);
      }
      return;
    }
    rest[key] = value;
  });

  batch.set(docRef, prepareData(rest as Partial<AppModelType>), { merge: true });

  if (presentFields.length > 0) {
    batch.set(docRef, prepareData(replacement as Partial<AppModelType>), {
      mergeFields: presentFields,
    });
  }
}
